import React, { useState, useRef, useCallback } from 'react';
import { X, Upload, FileText, AlertCircle, AlertTriangle, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  importSectionFromBase64,
  countAllComponents,
  validateSectionStructure,
} from '../../../utils/sectionExportImport';

/**
 * A single sortable item in the import preview list.
 * Renders the section filename, type badge, component count,
 * optional warning indicator, and a remove button.
 */
function SortableImportItem({ item, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sectionType = item.section?.section_type || 'Unknown';
  const componentCount = countAllComponents(item.section);
  const hasWarnings = item.warnings && item.warnings.length > 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-white border rounded-lg group ${
        isDragging ? 'shadow-lg border-blue-300' : 'border-gray-200'
      }`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* File icon */}
      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />

      {/* Filename and details */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{item.filename}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
            {sectionType}
          </span>
          {componentCount !== null && (
            <span className="text-xs text-gray-500">
              {componentCount} component{componentCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Warning indicator */}
      {hasWarnings && (
        <div className="flex-shrink-0" title={item.warnings.join('\n')}>
          <AlertTriangle className="w-4 h-4 text-amber-500" />
        </div>
      )}

      {/* Remove button */}
      <button
        onClick={() => onRemove(item.id)}
        className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100"
        aria-label={`Remove ${item.filename}`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/**
 * A failed file entry shown inline in the import list.
 * Displays the filename and error message with a dismiss button.
 */
function FailedFileEntry({ filename, error, onDismiss }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-red-700 truncate">{filename}</p>
        <p className="text-xs text-red-600 mt-0.5">{error}</p>
      </div>
      <button
        onClick={onDismiss}
        className="flex-shrink-0 p-1 text-red-400 hover:text-red-600 rounded-md transition-colors"
        aria-label="Dismiss error"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/**
 * Modal for importing one or more sections from base64-encoded data or files.
 * Supports pasting base64 text, uploading multiple .section/.json files,
 * and drag-and-drop reordering of the import queue before committing.
 *
 * The onImport callback always receives an array of section objects
 * in the order shown in the preview list.
 */
export default function ImportSectionModal({ isOpen, onClose, onImport }) {
  const [importItems, setImportItems] = useState([]);
  const [failedFiles, setFailedFiles] = useState([]);
  const [pasteText, setPasteText] = useState('');
  const [pasteError, setPasteError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const fileInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  // Generate a unique ID for each import item
  const generateId = () => crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  /**
   * Parse a single base64 string into a validated import item.
   * Returns the item object or throws with a descriptive error.
   */
  const parseBase64ToItem = useCallback((base64String, filename) => {
    const section = importSectionFromBase64(base64String.trim());
    const validation = validateSectionStructure(section);

    return {
      id: generateId(),
      filename,
      section,
      warnings: validation.warnings || [],
    };
  }, []);

  /**
   * Read and parse multiple files in parallel.
   * Successfully parsed files are added to importItems,
   * and failures are added to failedFiles.
   */
  const processFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    setIsProcessing(true);

    const readPromises = Array.from(files).map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const item = parseBase64ToItem(event.target.result, file.name);
            resolve({ success: true, item });
          } catch (err) {
            resolve({ success: false, filename: file.name, error: err.message || 'Failed to parse section data' });
          }
        };
        reader.onerror = () => {
          resolve({ success: false, filename: file.name, error: 'Failed to read file' });
        };
        reader.readAsText(file);
      });
    });

    const results = await Promise.all(readPromises);

    const newItems = [];
    const newFailures = [];

    for (const result of results) {
      if (result.success) {
        newItems.push(result.item);
      } else {
        newFailures.push({ id: generateId(), filename: result.filename, error: result.error });
      }
    }

    setImportItems((prev) => [...prev, ...newItems]);
    setFailedFiles((prev) => [...prev, ...newFailures]);
    setIsProcessing(false);
  }, [parseBase64ToItem]);

  if (!isOpen) {
    return null;
  }

  // Handle backdrop click to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle the "Add from paste" button
  const handleAddFromPaste = () => {
    if (!pasteText.trim()) return;

    try {
      const item = parseBase64ToItem(pasteText, 'Pasted section');
      setImportItems((prev) => [...prev, item]);
      setPasteText('');
      setPasteError('');
    } catch (err) {
      setPasteError(err.message || 'Invalid section data');
    }
  };

  // Handle file input change (multi-file)
  const handleFileSelect = async (e) => {
    await processFiles(e.target.files);
    // Reset so the same files can be selected again
    e.target.value = '';
  };

  // Handle drop zone click
  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  // Handle drag-and-drop of files onto the drop zone
  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(true);
  };

  const handleFileDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
  };

  const handleFileDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
    await processFiles(e.dataTransfer.files);
  };

  // Remove a single item from the import list
  const handleRemoveItem = (id) => {
    setImportItems((prev) => prev.filter((item) => item.id !== id));
  };

  // Dismiss a failed file entry
  const handleDismissFailure = (id) => {
    setFailedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // Handle dnd-kit reorder
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setImportItems((prev) => {
      const oldIndex = prev.findIndex((item) => item.id === active.id);
      const newIndex = prev.findIndex((item) => item.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  // Import all sections in order
  const handleImport = () => {
    if (importItems.length === 0) return;
    const sections = importItems.map((item) => item.section);
    onImport(sections);
    onClose();
  };

  const itemCount = importItems.length;
  const importButtonLabel = itemCount === 0
    ? 'Import Sections'
    : itemCount === 1
      ? 'Import 1 Section'
      : `Import ${itemCount} Sections`;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden" style={{ maxHeight: '85vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Import Sections</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 flex-1 overflow-auto min-h-0">
          {/* Paste base64 input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Paste Section Data
            </label>
            <div className="flex gap-2">
              <textarea
                value={pasteText}
                onChange={(e) => {
                  setPasteText(e.target.value);
                  if (pasteError) setPasteError('');
                }}
                placeholder="Paste base64-encoded section data here..."
                className="flex-1 h-20 p-3 text-xs font-mono bg-gray-50 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleAddFromPaste}
                disabled={!pasteText.trim()}
                className={`self-end px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  pasteText.trim()
                    ? 'text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200'
                    : 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
                }`}
              >
                Add
              </button>
            </div>
            {pasteError && (
              <div className="mt-2 flex items-start gap-2 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{pasteError}</span>
              </div>
            )}
          </div>

          {/* OR Divider */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* File Upload Drop Zone */}
          <div
            onClick={handleDropZoneClick}
            onDragOver={handleFileDragOver}
            onDragLeave={handleFileDragLeave}
            onDrop={handleFileDrop}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              isDraggingFile
                ? 'border-blue-400 bg-blue-50'
                : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".section,.json"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">
              {isProcessing ? (
                'Reading files...'
              ) : (
                <>
                  Click to upload or drag and drop
                  <br />
                  <span className="text-xs text-gray-400">.section or .json files (multiple allowed)</span>
                </>
              )}
            </p>
          </div>

          {/* Section Preview List */}
          {(importItems.length > 0 || failedFiles.length > 0) && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Sections to Import
                </label>
                {importItems.length > 1 && (
                  <span className="text-xs text-gray-500">
                    Drag to reorder
                  </span>
                )}
              </div>

              <div className="space-y-2 max-h-60 overflow-auto pr-1">
                {/* Failed file entries */}
                {failedFiles.map((failure) => (
                  <FailedFileEntry
                    key={failure.id}
                    filename={failure.filename}
                    error={failure.error}
                    onDismiss={() => handleDismissFailure(failure.id)}
                  />
                ))}

                {/* Sortable import items */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={importItems.map((item) => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {importItems.map((item) => (
                      <SortableImportItem
                        key={item.id}
                        item={item}
                        onRemove={handleRemoveItem}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={importItems.length === 0}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              importItems.length > 0
                ? 'text-white bg-blue-600 hover:bg-blue-700'
                : 'text-gray-400 bg-gray-200 cursor-not-allowed'
            }`}
          >
            {importButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
